import { Dot, Heart, Play, Star } from "lucide-react";
import { useEffect, useState } from "react";

import { Server } from "@wajeez/wajeez.microservices";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { useIsLoadingVisibile } from "@utils/hooks/useIsLoadingVisible";
import { Skeleton } from "@ui/skeleton";
import { OnItemPress } from "@modules/content/hooks/useComponentData";
import { msToHoursBalance } from "@utils/helpers/timeFormtter";
import { SVG } from "@components/items/common/SVG";
import {
  getCustomerQuota,
  setIsCreditModalOpen,
  useAppSelector,
  useFavorite,
} from "@lib/stores";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import TranslatedText from "@components/items/display/TranslatedText";

import CoinReplacmentDialog from "../b2c/content/CoinReplacmentDialog";
import SuccessReplacmentDialog from "../b2c/content/SuccessReplacmentDialog";

export interface RectangleReviewItemParameters {
  image: string | null | undefined;
  description: string | null | undefined;
  title: string | null | undefined;
}

type ItemParams = {
  id: string | undefined;
  title: string | undefined;
  authors: string[] | undefined;
  currentProgress?: number | undefined;
} & {
  [K in keyof RectangleReviewItemParameters]?:
    | RectangleReviewItemParameters[K]
    | undefined;
};

interface RectangleReviewItemProps {
  parameterValues: Partial<RectangleReviewItemParameters>;
  content:
    | (Server.ContentEntityOutput & { isLocked: boolean | undefined })
    | undefined
    | null;
  isLoading: boolean;
  onItemPress: OnItemPress<ItemParams>;
}

const RectangleReviewItem = ({
  parameterValues,
  content,
  isLoading,
  onItemPress,
}: RectangleReviewItemProps) => {
  const navigate = useAppNavigate();

  const [openReplacmentDialog, setOpenReplacmentDialog] =
    useState<boolean>(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState<boolean>(false);

  const customerQuota = useAppSelector((state) => state.payment.customerQuota);

  useEffect(() => {
    getCustomerQuota();
  }, []);

  const { isFavorite, handleFavoriteClick } = useFavorite(content);

  const { image, description, title } = parameterValues;

  const isLoadingVisible = useIsLoadingVisibile(isLoading && !content?.id);

  return (
    <div
      className={`w-full ${
        (isLoadingVisible || isLoading) && "h-[400px]"
      } px-10 pb-[35px] text-ramadan-primary dark:text-dark-ramadan-primary`}
    >
      <div
        className={`h-full w-full px-10 ${
          (isLoadingVisible || isLoading) && "bg-white dark:bg-black"
        }`}
      >
        <div className="h-full pb-[35px]">
          <RectangleReviewItemLoading isLoading={isLoadingVisible} />
        </div>
      </div>
      <SuccessReplacmentDialog
        open={openSuccessDialog}
        setOpen={setOpenSuccessDialog}
      />
      <div className="flex flex-col gap-6 pb-[53px] ps-5">
        <div className="text-[24px] font-bold dark:text-white">{title}</div>
        <div className="flex gap-10">
          <div
            className="relative cursor-pointer"
            onClick={() => {
              content && navigate(`/details/${content?.id}`);
            }}
          >
            <img
              src={getPublicMediaUrl(image)}
              alt="suggested book"
              className="h-[393px] w-[265px] rounded-lg border border-black border-opacity-20 shadow-[10px_10px_10px_rgba(0,0,0,0.35)] hover:opacity-90"
            />
            {content?.isLocked && (
              <div className="absolute bottom-[11px] left-[15px] flex h-[55px] w-[55px] items-center justify-center rounded-full bg-[linear-gradient(180deg,_rgba(255,_204,_101,_0.90)_0%,_rgba(255,_191,_62,_0.95)_100%)]">
                <img
                  src="/assets/icons/premium.svg"
                  alt="premium"
                  className="h-[40px] w-[40px]"
                />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-5 py-5">
            <div
              className="cursor-pointer text-[24px] font-bold hover:opacity-85 dark:text-white"
              onClick={() => {
                content && navigate(`/details/${content?.id}`);
              }}
            >
              {content?.title}
            </div>
            <div className="mb-5 line-clamp-6 text-[20px] text-[#6D6D6D] sm:max-h-[393px] sm:max-w-[740px] dark:text-[#979797]">
              {description}
            </div>
            <div className="flex flex-col">
              <div className="gap-x mt-3 flex items-center justify-start text-[16px] font-medium text-[#6D6D6D] dark:text-[#979797]">
                <div>{content?.authors?.join(", ")}</div>
                <Dot className="text-black" />
                <div>{msToHoursBalance(content?.durationMS || 0)}</div>
                {content?.rate && (
                  <>
                    <Dot className="text-black" />
                    <div className="flex items-center justify-center gap-1">
                      <Star
                        size={15}
                        className="fill-[#727272] text-[#727272]"
                      />

                      <div className="dark:text-white">
                        {content?.rate?.toFixed(1)}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center gap-16 py-5">
                {!content?.isLocked ? (
                  <ShadcnButton
                    className="flex items-center justify-center gap-1 rounded-full bg-[#DDE9EA] px-6 py-7 text-[20px] font-medium hover:bg-[#DDE9EA] hover:opacity-85 dark:text-black"
                    onClick={() => {
                      content && navigate(`/details/${content?.id}`);
                    }}
                  >
                    <Play className={`fill-black`} />
                    <TranslatedText text={`full-books.listen`} />
                  </ShadcnButton>
                ) : customerQuota?.data?.remainingCredit !== undefined &&
                  customerQuota?.data?.remainingCredit > 0 ? (
                  <CoinReplacmentDialog
                    trigger={
                      <ShadcnButton
                        className="flex items-center justify-center gap-1 rounded-full bg-[#DDE9EA] px-6 py-7 text-[20px] font-medium hover:bg-[#DDE9EA] hover:opacity-85 dark:text-[#0C0C0C]"
                        onClick={() => setOpenReplacmentDialog(true)}
                      >
                        <TranslatedText text="full-books.replace" />
                        <SVG src="/assets/icons/coin.svg" className="h-6 w-6" />
                        <div>1</div>
                      </ShadcnButton>
                    }
                    open={openReplacmentDialog}
                    setOpen={setOpenReplacmentDialog}
                    setOpenSuccessDialog={setOpenSuccessDialog}
                    id={content?.id}
                  />
                ) : (
                  <ShadcnButton
                    className="flex items-center justify-center gap-1 rounded-full bg-[#DDE9EA] px-6 py-7 text-[20px] font-medium hover:bg-[#DDE9EA] hover:opacity-85 dark:text-[#0C0C0C]"
                    onClick={() => {
                      setIsCreditModalOpen(true);
                    }}
                  >
                    <SVG src="/assets/icons/coin.svg" className="h-6 w-6" />
                    <TranslatedText text="full-books.recharge" />
                  </ShadcnButton>
                )}
                <Heart
                  size={40}
                  className={`cursor-pointer ${isFavorite && "fill-black dark:fill-white"} dark:text-white`}
                  onClick={handleFavoriteClick}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RectangleReviewItemLoading = ({ isLoading }: { isLoading: boolean }) => {
  return isLoading ? (
    <div className="mx-auto flex w-full flex-col justify-center gap-3 pb-5">
      <Skeleton className="h-5 w-20" />
      <Skeleton className="h-5 w-[200px]" />
      <Skeleton className={`mt-7 h-[304px] w-full rounded-[12px]`} />
    </div>
  ) : null;
};

export default RectangleReviewItem;
